'use client';

import type { EventContentArg } from '@fullcalendar/core';
import { CalendarDeadlineCheckbox } from '@/app/(main)/calendar/components/CalendarEventContent/CalendarDeadlineCheckbox';
import { CALENDAR_EVENT_TYPES } from '@/app/(main)/calendar/consts/calendar-event-types';
import { CALENDAR_TASK_STATUS } from '@/app/(main)/calendar/consts/calendar-task-status';
import { formatCalendarEventDisplayTitle } from '@/app/(main)/calendar/utils/formatCalendarEventDisplayTitle';
import {
  formatBirthdaysAriaLabel,
  formatGroupedBirthdayLabel,
} from '@/app/(main)/calendar/utils/formatGroupedBirthdayLabel';
import {
  CALENDAR_EVENT_EXTENDED_PROP,
  getGroupedBirthdays,
} from '@/app/(main)/calendar/utils/mapCalendarEventsToFullCalendar';
import type { CalendarEvent } from '@/types/calendarTypes';
import BirthdayIcon from '/public/icons/birthday.svg';
import CalendarIcon from '/public/icons/calendar.svg';

type CalendarEventContentProps = {
  arg: EventContentArg;
  isMobile?: boolean;
  onToggleTaskComplete?: (taskId: string, completed: boolean) => void;
};

export function CalendarEventContent({ arg, isMobile = false, onToggleTaskComplete }: CalendarEventContentProps) {
  const ev = arg.event.extendedProps[CALENDAR_EVENT_EXTENDED_PROP] as CalendarEvent | undefined;
  if (!ev) return null;

  const displayTitle = formatCalendarEventDisplayTitle(ev);

  if (ev.type === CALENDAR_EVENT_TYPES.BIRTHDAY) {
    const groupedBirthdays = getGroupedBirthdays(arg.event, ev);
    const label = formatGroupedBirthdayLabel(groupedBirthdays, { countOnly: isMobile });

    return (
      <div className="sf-calendar-event__content flex items-center gap-1 w-full min-w-0 overflow-hidden pl-[6px] pr-1">
        <BirthdayIcon className="h-3.5 w-3.5 shrink-0 text-status-success" aria-hidden />
        <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-body-small text-status-success">
          {isMobile ? (
            <>
              <span aria-hidden>{label}</span>
              <span className="sr-only">{formatBirthdaysAriaLabel(groupedBirthdays)}</span>
            </>
          ) : (
            label
          )}
        </span>
      </div>
    );
  }

  if (ev.type === CALENDAR_EVENT_TYPES.TASK_REMINDER) {
    const isCompleted = ev.status === CALENDAR_TASK_STATUS.COMPLETED;
    const isClosed = ev.status === CALENDAR_TASK_STATUS.CLOSED;

    return (
      <div
        className={`sf-calendar-event__content sf-calendar-event__content--${CALENDAR_EVENT_TYPES.TASK_REMINDER} flex items-center gap-1 text-left w-full min-w-0 overflow-hidden pl-0.5 pr-1`}
      >
        <span className="sf-calendar-event__leading flex h-4 w-4 shrink-0 items-center justify-center">
          <CalendarIcon className="sf-calendar-event__task-icon h-4 w-4 shrink-0 text-blue-300" aria-hidden />
        </span>
        <span
          className={`min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-body-small ${
            isClosed ? 'text-grey-500' : isCompleted ? 'line-through text-grey-500' : 'text-blue-300'
          }`}
        >
          {displayTitle}
        </span>
      </div>
    );
  }

  if (ev.type === CALENDAR_EVENT_TYPES.TASK_DEADLINE) {
    const isCompleted = ev.status === CALENDAR_TASK_STATUS.COMPLETED;
    const isClosed = ev.status === CALENDAR_TASK_STATUS.CLOSED;
    const showCheckbox = Boolean(onToggleTaskComplete && ev.taskId && !isClosed);

    return (
      <div
        className={`sf-calendar-event__content sf-calendar-event__content--${CALENDAR_EVENT_TYPES.TASK_DEADLINE} flex items-center gap-1 text-left w-full min-w-0 overflow-hidden pl-0.5 pr-1`}
      >
        <span className="sf-calendar-event__leading relative flex h-4 w-4 shrink-0 items-center justify-center">
          {showCheckbox ? (
            <CalendarDeadlineCheckbox
              checked={isCompleted}
              onToggle={() => {
                if (!ev.taskId || !onToggleTaskComplete) return;
                onToggleTaskComplete(ev.taskId, !isCompleted);
              }}
            />
          ) : null}
          <CalendarIcon className="sf-calendar-event__task-icon h-4 w-4 shrink-0 text-blue-300" aria-hidden />
        </span>
        <span
          className={`min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-body-small ${
            isClosed ? 'text-grey-500' : isCompleted ? 'line-through text-grey-500' : 'text-blue-300'
          }`}
        >
          {displayTitle}
        </span>
      </div>
    );
  }

  const isDark = ev.type === CALENDAR_EVENT_TYPES.OUTLOOK_IN_PERSON;
  return (
    <div className="sf-calendar-event__content w-full min-w-0 overflow-hidden">
      <span
        className={`block overflow-hidden text-ellipsis whitespace-nowrap text-body-small ${isDark ? 'text-blue-300 font-medium' : ''}`}
      >
        {displayTitle}
      </span>
    </div>
  );
}

export function renderCalendarEventContent(
  arg: EventContentArg,
  options?: Pick<CalendarEventContentProps, 'isMobile' | 'onToggleTaskComplete'>
) {
  return (
    <CalendarEventContent arg={arg} isMobile={options?.isMobile} onToggleTaskComplete={options?.onToggleTaskComplete} />
  );
}
